import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliBadge, LiliBadgeType } from '@lili/lili-frontend-common';
import Icon from '../../../../generic-components/Icon';
import Heading from '../../../../generic-components/Heading';
import Button from '../../../../generic-components/Button';
import downArrowIcon from '../../../../assets/svg/Down-arrow.svg';
import downArrowBlueIcon from '../../../../assets/svg/arrow-down-blue.svg';
import ErrorIcon from '../../../../assets/icons/svg/stamp-icons/ic-stamp-alert.svg';
import ToggleButton from './ToggleButton';
import { STYLES } from '../consts/consts';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';

const {
  PAGES: {
    PAYMENTS: { PAYMENTS_LIST },
  },
} = STYLES;

interface IProps {
  isExpand: boolean;
  allowActive?: boolean;
  handleToggleExpand: () => void;
  handleSetSelectedPayment: (type: string, selected: boolean) => void;
  previewData: {
    title: string;
    subTitle: string;
    logoName: string;
    isRecommended: boolean;
    activated: boolean;
    selected: boolean;
    type: string;
    accountStatus: string;
  };
}

export const PaymentItemPreview = ({
  isExpand,
  handleToggleExpand,
  previewData,
  handleSetSelectedPayment,
  allowActive,
}: IProps) => {
  const [toggleValue, setToggleValue] = useState<boolean>(previewData?.selected || false);
  const [isLimitReachedErrorShown, setIsLimitReachedErrorShown] = useState<boolean>(false);
  const { t } = useTranslation();
  const getLogoUrl = () => `https://assets.lili.co/unified/paymentoptions/${previewData.logoName}.png`;
  const handleToggle = async () => {
    if (!allowActive && !previewData.selected) {
      setIsLimitReachedErrorShown(true);
    } else {
      setToggleValue(!previewData.selected);
      handleSetSelectedPayment(previewData.type, !previewData.selected);
    }
  };

  const elPId = `elPManage-${previewData.type}`;
  const elImageArrowId = `elImageArrow-${previewData.type}`;

  useEffect(() => {
    const elImageArrow = document.getElementById(elImageArrowId);

    if (previewData.activated && isExpand) {
      if (elImageArrow) elImageArrow.classList.add('animate-rotateChevron');
    }
    if (previewData.activated && !isExpand) {
      if (elImageArrow) elImageArrow.classList.remove('animate-rotateChevron');
    }
  }, [isExpand]);

  useEffect(() => {
    const elSpan = document.getElementById(elPId);
    const elImageArrow = document.getElementById(elImageArrowId);

    if (previewData.activated && elSpan) {
      elSpan.style.display = 'inline-block';
    }
    if (previewData.activated && elImageArrow) {
      elImageArrow.style.display = 'inline-block';
      elImageArrow.setAttribute('src', downArrowBlueIcon);
    }
  }, []);

  const getStatusText = () => {
    if (
      previewData.accountStatus &&
      previewData.accountStatus !== 'READY' &&
      previewData.accountStatus !== 'KYC_EVALUATION'
    ) {
      return t('payment_account_continue_registration', { defaultValue: 'Action required' });
    }
    if (previewData.accountStatus === 'KYC_EVALUATION') {
      return t('payment_account_in_review', { defaultValue: 'In review' });
    }
    if (previewData.accountStatus !== 'READY' && previewData.isRecommended) {
      return t('payment_account_continue_recommended', { defaultValue: 'Recommended' });
    }
    return '';
  };

  return (
    <div
      id={`itemWrapper-${previewData.type}`}
      role='button'
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={handleToggleExpand}
      className={PAYMENTS_LIST.PAYMENT_ITEM_PREVIEW_CONTAINER}
    >
      {isLimitReachedErrorShown && (
        <UserMessage onClick={() => setIsLimitReachedErrorShown(false)} id='limitReachedUserMessage'>
          <div className='fadeIn flex flex-col items-center py-[30px] px-[40px] bg-bw00 rounded-[4px]'>
            <Icon src={ErrorIcon} className='w-[70px] mb-[12px]' />
            <Heading title={t('LIMIT_REACHED')} className='font-metricR text-[18px] text-bw08 mb-[8px]' />
            <Heading
              title={t('LIMIT_REACHED_disclaimer')}
              className='font-metricR text-[14px] text-bw07 mb-[22px] max-w-[240px] text-center'
            />
            <Button
              onClick={() => setIsLimitReachedErrorShown(false)}
              className='flex items-center justify-center bg-action03 hover:bg-action04 h-[54px] w-full rounded-[4px]'
              id='limitReachCta'
              childElement={<Heading title='OK' className='font-metricRSemibold text-bw00 text-[16px]' />}
            />
          </div>
        </UserMessage>
      )}
      <div className={PAYMENTS_LIST.PAYMENT_ITEM_PREVIEW_LOGO_WRAPPER}>
        <Icon className={PAYMENTS_LIST.PAYMENT_ITEM_PREVIEW_LOGO} src={getLogoUrl()} />
      </div>
      <div className={PAYMENTS_LIST.PAYMENT_ITEM_PREVIEW_CONTENT_WRAPPER}>
        <div className='flex items-center '>
          <Heading title={previewData.title} className={`${PAYMENTS_LIST.PAYMENT_ITEM_PREVIEW_MAIN_TITLE} `} />
          {getStatusText() && <LiliBadge id='statusBadge' title={getStatusText()} type={LiliBadgeType.NEUTRAL} />}
        </div>

        <h1
          className={PAYMENTS_LIST.PAYMENT_ITEM_PREVIEW_SUB_TITLE}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{
            __html: `${previewData.subTitle}
            <span id="${elPId}" class="hidden" style="color: #0E5CF4">
            Manage
            </span>
            <span> 
            <img id="${elImageArrowId}" class="w-[10px]"  /> 
            </span>`,
          }}
        />
      </div>
      {!previewData.activated && (
        <Button
          onClick={handleToggleExpand}
          className='basis-[17px] min-w-[17px] pr-[5px]'
          id='toggleExpand'
          childElement={
            <Icon className={`${isExpand ? 'animate-rotateChevron' : ''} w-full h-full`} src={downArrowIcon} />
          }
        />
      )}
      {previewData.activated && (
        <ToggleButton
          styles={`min-w-[35px] max-w-[35px] ${previewData.accountStatus !== 'READY' ? 'disabled' : ''}`}
          toggleState={previewData.accountStatus !== 'READY' ? false : toggleValue}
          toggle={handleToggle}
          buttonId={`paymentToggle-${previewData.type}`}
          disabled={previewData.accountStatus !== 'READY'}
          isSpinner={false}
        />
      )}
    </div>
  );
};
